import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navbar } from "components/layout/components/Navbar";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import avatar from "assets/images/default.webp";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { END_POINT } from "config/environment";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useLocation, useNavigate } from "react-router-dom";
import { routePath } from "../../../config/routeMgt/routePath";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { useQueryClient } from "react-query";
import { QUERY_KEY_FOR_UNREAD_MESSAGES } from "../hooks/useGetUnreadMessageCount";

dayjs.extend(relativeTime);

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const POLL_INTERVAL_MS = 4000;

interface TalentMessageSummary {
  user_id: string;
  talent_id: string;
  name: string;
  photo: string | null;
  current_job: string | null;
  job_title: string | null;
  employer: string | null;
  executive_summary: string | null;
}

const formatSummaryValue = (value?: string | null) =>
  value && value.trim() ? value : "—";

const normalizeMessage = (raw: any, fallbackUserId?: string) => {
  if (!raw) return null;
  const message = raw.message ?? raw;
  if (!message?.id) return null;

  return {
    ...message,
    user: message.user ?? { id: message.user_id ?? fallbackUserId },
    seen: Boolean(message.seen),
  };
};

const Messages: React.FC = () => {
  const [chatlists, setChatlists] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [text, setText] = useState("");
  const { token, userInfo } = useGetUserInfo();
  const [to_user_id, setToUser] = useState<any>({});
  const [talentSummary, setTalentSummary] =
    useState<TalentMessageSummary | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [socketConnected, setSocketConnected] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const echoRef = useRef<Echo<any> | null>(null);
  const activeChannelRef = useRef<string | null>(null);
  const markingSeenRef = useRef(false);

  const chat_id = new URLSearchParams(location.search).get("id");

  const appendIncomingMessage = useCallback(
    (incoming: any) => {
      const message = normalizeMessage(incoming);
      if (!message) return;

      setMessages((prev) => {
        if (prev.some((item) => item.id === message.id)) return prev;
        return [...prev, message];
      });

      const isForMe = String(message.to_user_id) === String(userInfo?.id);
      moveChatToTop(String(message.chat_list_id ?? chat_id), message, {
        bumpUnread: isForMe && String(message.chat_list_id) !== String(chat_id),
      });

      if (isForMe && String(message.chat_list_id) === String(chat_id)) {
        markMessageAsSeen(message.id);
        setChatlists((prev) =>
          prev.map((chat) =>
            String(chat.id) === String(chat_id)
              ? { ...chat, unread_count: 0 }
              : chat,
          ),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chat_id, userInfo?.id],
  );

  const mergeMessages = useCallback((incoming: any[]) => {
    const normalized = incoming
      .map((item) => normalizeMessage(item))
      .filter(Boolean) as any[];

    setMessages((prev) => {
      const byId = new Map(prev.map((item) => [item.id, item]));
      let changed = false;

      normalized.forEach((item) => {
        const existing = byId.get(item.id);
        if (!existing) {
          byId.set(item.id, item);
          changed = true;
          return;
        }
        if (Boolean(existing.seen) !== Boolean(item.seen)) {
          byId.set(item.id, { ...existing, ...item });
          changed = true;
        }
      });

      if (!changed && byId.size === prev.length) return prev;

      return Array.from(byId.values()).sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
    });
  }, []);

  const getChatList = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${END_POINT.BASE_URL}/v1/chatlists`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const data = await response.json();
      const chatArray = Array.isArray(data) ? data : [];
      setChatlists(chatArray);

      if (chat_id) {
        const currentChat = chatArray.find(
          (item: any) => String(item.id) === String(chat_id),
        );
        if (currentChat?.participants) setToUser(currentChat.participants);
      }
    } catch (error) {
      console.error("Error fetching chat list:", error);
      setChatlists([]);
    }
  }, [token, chat_id]);

  const getMessages = useCallback(async () => {
    if (!token || !chat_id) return;

    try {
      const response = await fetch(
        `${END_POINT.BASE_URL}/v1/message/${chat_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        },
      );
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      mergeMessages(list);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  }, [token, chat_id, mergeMessages]);

  useEffect(() => {
    getChatList();
  }, [getChatList]);

  useEffect(() => {
    if (!token || !to_user_id?.id) {
      setTalentSummary(null);
      setIsSummaryLoading(false);
      return;
    }

    if (userInfo?.type !== "recruiter" || to_user_id?.type !== "talent") {
      setTalentSummary(null);
      setIsSummaryLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSummary = async () => {
      setIsSummaryLoading(true);
      try {
        const response = await fetch(
          `${END_POINT.BASE_URL}/v1/talent-message-summary/${to_user_id.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          },
        );

        if (!response.ok) {
          if (!cancelled) setTalentSummary(null);
          return;
        }

        const data: TalentMessageSummary = await response.json();
        if (!cancelled) setTalentSummary(data);
      } catch (error) {
        console.error("Error fetching talent summary:", error);
        if (!cancelled) setTalentSummary(null);
      } finally {
        if (!cancelled) setIsSummaryLoading(false);
      }
    };

    setTalentSummary(null);
    fetchSummary();

    return () => {
      cancelled = true;
    };
  }, [token, to_user_id?.id, to_user_id?.type, userInfo?.type]);

  useEffect(() => {
    if (!chat_id || !token) return;

    setMessages([]);
    getMessages();
    connectToChannel(chat_id);

    return () => {
      leaveActiveChannel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat_id, token]);

  // Polling fallback so replies still appear if websockets fail
  useEffect(() => {
    if (!chat_id || !token) return;

    const timer = window.setInterval(() => {
      getMessages();
      if (!socketConnected) getChatList();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [chat_id, token, socketConnected, getMessages, getChatList]);

  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!messages.length || !chat_id) return;
    markMessagesAsSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, chat_id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEmojiPicker]);

  const leaveActiveChannel = () => {
    if (echoRef.current && activeChannelRef.current) {
      echoRef.current.leave(activeChannelRef.current);
      activeChannelRef.current = null;
    }
  };

  const connectToChannel = (activeChatId: string) => {
    if (!token) return;

    window.Pusher = Pusher as any;

    if (!echoRef.current) {
      const reverbKey =
        process.env.REACT_APP_REVERB_APP_KEY || "1f5b0a0c1b28fca642d4";
      const reverbHost =
        process.env.REACT_APP_REVERB_HOST || window.location.hostname;
      const reverbPort = process.env.REACT_APP_REVERB_PORT || "8080";
      const isSecure =
        window.location.protocol === "https:" ||
        process.env.REACT_APP_REVERB_SCHEME === "https";

      echoRef.current = new Echo({
        broadcaster: "reverb",
        key: reverbKey,
        wsHost: reverbHost,
        wsPort: Number(reverbPort),
        wssPort: 443,
        forceTLS: isSecure,
        enabledTransports: isSecure ? ["wss"] : ["ws"],
        authorizer: (channel: { name: string }) => ({
          authorize: (
            socketId: string,
            callback: (error: boolean, data: any) => void,
          ) => {
            axios
              .post(
                `${END_POINT.AUTH_URL}/broadcasting/auth`,
                { socket_id: socketId, channel_name: channel.name },
                { headers: { Authorization: `Bearer ${token}` } },
              )
              .then((response) => callback(false, response.data))
              .catch((error) => {
                setSocketConnected(false);
                callback(true, error);
              });
          },
        }),
      });

      echoRef.current.connector.pusher.connection.bind("connected", () => {
        setSocketConnected(true);
      });
      echoRef.current.connector.pusher.connection.bind("disconnected", () => {
        setSocketConnected(false);
      });
      echoRef.current.connector.pusher.connection.bind("unavailable", () => {
        setSocketConnected(false);
      });
    }

    leaveActiveChannel();

    const channelName = `chat.${activeChatId}`;
    activeChannelRef.current = channelName;

    echoRef.current
      .private(channelName)
      .listen(".MessageSent", (event: any) => {
        appendIncomingMessage(event);
      });
  };

  useEffect(() => {
    return () => {
      leaveActiveChannel();
      if (echoRef.current) {
        echoRef.current.disconnect();
        echoRef.current = null;
      }
    };
  }, []);

  const markMessagesAsSeen = async () => {
    if (!token || !chat_id || markingSeenRef.current) return;

    const unseenIds = messages
      .filter(
        (msg) => String(msg.to_user_id) === String(userInfo?.id) && !msg.seen,
      )
      .map((msg) => msg.id);

    if (!unseenIds.length) {
      setChatlists((prev) => {
        const current = prev.find(
          (chat) => String(chat.id) === String(chat_id),
        );
        if (!current || !current.unread_count) return prev;
        return prev.map((chat) =>
          String(chat.id) === String(chat_id)
            ? { ...chat, unread_count: 0 }
            : chat,
        );
      });
      return;
    }

    markingSeenRef.current = true;
    try {
      await axios.post(
        `${END_POINT.BASE_URL}/v1/messages/mark-seen`,
        {
          chat_list_id: chat_id,
          message_ids: unseenIds,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setMessages((prev) =>
        prev.map((msg) =>
          unseenIds.includes(msg.id) ? { ...msg, seen: true } : msg,
        ),
      );
      setChatlists((prev) =>
        prev.map((chat) =>
          String(chat.id) === String(chat_id)
            ? { ...chat, unread_count: 0 }
            : chat,
        ),
      );
      queryClient.invalidateQueries([QUERY_KEY_FOR_UNREAD_MESSAGES]);
    } catch (error) {
      console.error("Error marking messages as seen:", error);
    } finally {
      markingSeenRef.current = false;
    }
  };

  const markMessageAsSeen = async (messageId: string) => {
    if (!token) return;
    try {
      await axios.post(
        `${END_POINT.BASE_URL}/v1/messages/${messageId}/mark-seen`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, seen: true } : msg,
        ),
      );
    } catch (error) {
      console.error("Error marking message as seen:", error);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!text.trim() || !to_user_id?.id || !token) return;

    const optimisticText = text.trim();
    setText("");

    try {
      const response = await axios.post(
        `${END_POINT.BASE_URL}/v1/sendmessage`,
        { to_user_id: to_user_id.id, text: optimisticText },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const sentMessage = normalizeMessage(response.data, userInfo?.id);
      if (!sentMessage) return;

      setMessages((prev) => {
        if (prev.some((item) => item.id === sentMessage.id)) return prev;
        return [...prev, sentMessage];
      });
      moveChatToTop(chat_id!, sentMessage);
    } catch (error) {
      console.error("Error sending message:", error);
      setText(optimisticText);
    }
  };

  const groupMessagesByDate = (messagesData: any[]) => {
    return messagesData.reduce((acc: Record<string, any[]>, message) => {
      const date = dayjs(message.created_at).format("YYYY-MM-DD");
      if (!acc[date]) acc[date] = [];
      acc[date].push(message);
      return acc;
    }, {});
  };

  const formatDate = (date: string) => {
    const today = dayjs().startOf("day");
    const messageDate = dayjs(date, "YYYY-MM-DD");
    const diffDays = today.diff(messageDate, "day");

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return DAYS[messageDate.day()];
    return messageDate.format("ddd, MMM D YYYY");
  };

  const filteredChatlists = Array.isArray(chatlists)
    ? chatlists.filter((contact: any) =>
        (contact.participants?.name || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      )
    : [];

  const moveChatToTop = (
    chatId: string,
    newMessage?: any,
    options?: { bumpUnread?: boolean },
  ) => {
    setChatlists((prev) => {
      const updated = [...prev];
      const index = updated.findIndex(
        (chat) => String(chat.id) === String(chatId),
      );
      if (index === -1) {
        getChatList();
        return prev;
      }

      const chatItem = { ...updated[index] };

      if (newMessage) {
        chatItem.last_message = newMessage.text;
        chatItem.last_message_time =
          newMessage.created_at ?? new Date().toISOString();
        chatItem.last_message_user_id = newMessage.user_id;
        if (options?.bumpUnread) {
          chatItem.unread_count = (chatItem.unread_count || 0) + 1;
        }
      }

      updated.splice(index, 1);
      updated.unshift(chatItem);
      return updated;
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
  };

  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    setText((prev) => `${prev}${emojiData.emoji}`);
  };

  const isMessageMine = (message: any) =>
    String(message.user?.id ?? message.user_id) === String(userInfo?.id);

  return (
    <DashboardLayout container={false}>
      <div className="flex h-[calc(100vh-0.5rem)] flex-col overflow-hidden px-3 md:px-6">
        <div className="shrink-0">
          <Navbar title="Message" description="" />
        </div>

        <div className="mx-1 mb-3 min-h-0 flex-1 overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div
            className={`grid h-full min-h-0 grid-cols-1 ${
              userInfo?.type === "recruiter"
                ? "lg:grid-cols-[30%_50%_20%]"
                : "lg:grid-cols-[30%_70%]"
            }`}
          >
            {/* ================= LEFT PANEL ================= */}
            <div className="flex h-full min-h-0 flex-col overflow-hidden border-r border-gray-200">
              <div className="shrink-0 p-4">
                <div className="flex items-center overflow-hidden rounded-lg border border-gray-200">
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-12 w-full px-4 focus:outline-none"
                  />
                  <button
                    type="button"
                    className="flex h-12 w-14 items-center justify-center bg-primary"
                  >
                    <i className="ri-search-line text-xl text-white"></i>
                  </button>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
                {filteredChatlists.map((contact: any) => {
                  const unread = Number(contact.unread_count || 0);
                  const isActive = String(chat_id) === String(contact.id);

                  return (
                    <div
                      key={contact.id}
                      className={`mb-1 flex cursor-pointer items-center gap-3 rounded-lg p-3 ${
                        isActive ? "bg-gray-100" : "hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        navigate(`/message?id=${contact.id}`);
                        setToUser(contact.participants);
                        setChatlists((prev) =>
                          prev.map((chat) =>
                            String(chat.id) === String(contact.id)
                              ? { ...chat, unread_count: 0 }
                              : chat,
                          ),
                        );
                      }}
                    >
                      <div className="relative h-10 w-10 shrink-0">
                        <img
                          src={
                            contact.participants?.photo
                              ? contact.participants.photo
                              : avatar
                          }
                          alt="User Avatar"
                          className="h-10 w-10 rounded-full object-cover"
                        />
                        <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-green-500"></span>
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h2
                            className={`truncate text-sm ${
                              unread > 0
                                ? "font-semibold text-gray-900"
                                : "font-medium text-gray-800"
                            }`}
                          >
                            {contact.participants?.name}
                          </h2>

                          <div className="flex shrink-0 items-center gap-2">
                            {contact.last_message_time && (
                              <span
                                className={`text-xs ${
                                  unread > 0
                                    ? "font-semibold text-primary"
                                    : "text-gray-500"
                                }`}
                              >
                                {dayjs(contact.last_message_time).format(
                                  "HH:mm",
                                )}
                              </span>
                            )}
                            {unread > 0 && (
                              <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                {unread > 99 ? "99+" : unread}
                              </span>
                            )}
                          </div>
                        </div>

                        <p
                          className={`mt-1 truncate text-sm ${
                            unread > 0
                              ? "font-medium text-gray-800"
                              : "text-gray-500"
                          }`}
                        >
                          {contact.last_message || "No messages yet"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ================= CENTER PANEL ================= */}
            <div className="flex h-full min-h-0 flex-col overflow-hidden border-r border-gray-200">
              <header className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-white p-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <img
                      src={to_user_id?.photo ? to_user_id.photo : avatar}
                      alt="User Avatar"
                      className="h-10 w-10 rounded-full object-cover"
                    />
                    {to_user_id?.online === 1 && (
                      <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-green-500"></span>
                    )}
                  </div>

                  <div>
                    <h1 className="text-sm font-semibold text-gray-800">
                      {to_user_id?.name || "Select a conversation"}
                    </h1>
                    {to_user_id?.name && (
                      <div className="mt-1 flex items-center gap-1">
                        <span
                          className={`h-2 w-2 rounded-full ${
                            to_user_id?.online === 1
                              ? "bg-green-500"
                              : "bg-gray-400"
                          }`}
                        ></span>
                        <span className="text-xs text-gray-500">
                          {to_user_id?.online === 1 ? "Online" : "Offline"}
                          {!socketConnected ? " · syncing…" : ""}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </header>

              <div
                className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-5"
                ref={messagesScrollRef}
              >
                {!chat_id ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-400">
                    Choose a chat from the list to start messaging.
                  </div>
                ) : (
                  Object.entries(groupMessagesByDate(messages)).map(
                    ([date, groupedMessages]) => (
                      <div key={date}>
                        <div className="my-5 flex items-center gap-4">
                          <div className="h-px flex-1 bg-gray-200"></div>
                          <span className="text-sm text-gray-500">
                            {formatDate(date)}
                          </span>
                          <div className="h-px flex-1 bg-gray-200"></div>
                        </div>

                        {(groupedMessages as any[]).map((message) => {
                          const isMine = isMessageMine(message);

                          return (
                            <div
                              key={message.id}
                              className={`mb-5 flex ${
                                isMine ? "justify-end" : "justify-start"
                              }`}
                            >
                              {!isMine && (
                                <img
                                  src={
                                    to_user_id?.photo
                                      ? to_user_id.photo
                                      : avatar
                                  }
                                  alt="Avatar"
                                  className="mr-3 mt-1 h-9 w-9 rounded-full object-cover"
                                />
                              )}

                              <div className="flex items-end gap-2">
                                {!isMine ? (
                                  <>
                                    <div className="max-w-[75%] rounded-2xl bg-white px-4 py-3 text-sm text-gray-800">
                                      {message.text}
                                    </div>
                                    <span className="whitespace-nowrap text-xs text-gray-500">
                                      {dayjs(message.created_at).format(
                                        "h:mm A",
                                      )}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <div className="flex flex-col items-end gap-1">
                                      <span className="whitespace-nowrap text-xs text-gray-500">
                                        {dayjs(message.created_at).format(
                                          "h:mm A",
                                        )}
                                      </span>
                                      <span className="text-[10px] text-gray-400">
                                        {message.seen ? "Read" : "Sent"}
                                      </span>
                                    </div>
                                    <div className="max-w-[75%] rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-3 text-sm text-white">
                                      {message.text}
                                    </div>
                                  </>
                                )}
                              </div>

                              {isMine && (
                                <img
                                  src={
                                    userInfo?.photo ? userInfo.photo : avatar
                                  }
                                  alt="Avatar"
                                  className="ml-3 mt-1 h-9 w-9 rounded-full object-cover"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ),
                  )
                )}
              </div>

              <footer className="shrink-0 border-t border-gray-100 bg-white p-4">
                {selectedFile && (
                  <div className="mb-3 flex items-center gap-3">
                    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                      <i
                        className={
                          selectedFile.type.startsWith("image/")
                            ? "ri-image-line text-lg text-gray-500"
                            : "ri-file-line text-lg text-gray-500"
                        }
                      ></i>
                      <span className="max-w-[200px] truncate text-sm text-gray-700">
                        {selectedFile.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                        }}
                        aria-label="Remove attachment"
                      >
                        <i className="ri-close-line text-lg text-gray-500"></i>
                      </button>
                    </div>
                  </div>
                )}

                <div className="relative">
                  {showEmojiPicker && (
                    <div
                      ref={emojiPickerRef}
                      className="absolute bottom-[65px] right-8 z-50"
                    >
                      <EmojiPicker
                        onEmojiClick={handleEmojiSelect}
                        width={320}
                        height={400}
                        searchPlaceHolder="Search emoji"
                        previewConfig={{ showPreview: false }}
                      />
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelect}
                    accept="image/*,.pdf,.doc,.docx"
                  />

                  <form
                    onSubmit={handleSubmit}
                    className="flex h-14 items-center rounded-2xl border border-gray-200 bg-white px-4"
                  >
                    <input
                      type="text"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder={
                        chat_id
                          ? "Write your message..."
                          : "Select a conversation first"
                      }
                      disabled={!chat_id || !to_user_id?.id}
                      className="flex-1 bg-transparent text-sm outline-none disabled:cursor-not-allowed"
                    />

                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker((prev) => !prev)}
                      aria-label="Add emoji"
                      disabled={!chat_id}
                      className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100 disabled:opacity-40"
                    >
                      <i className="ri-emotion-happy-line text-xl text-gray-500"></i>
                    </button>

                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      aria-label="Attach file"
                      disabled={!chat_id}
                      className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100 disabled:opacity-40"
                    >
                      <i className="ri-attachment-2 text-xl text-gray-500"></i>
                    </button>

                    <button
                      type="submit"
                      disabled={!chat_id || !text.trim()}
                      aria-label="Send message"
                      className="ml-1 flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white disabled:opacity-40"
                    >
                      <i className="ri-send-plane-2-fill text-base"></i>
                    </button>
                  </form>
                </div>
              </footer>
            </div>

            {/* ================= RIGHT PANEL (RECRUITER ONLY) ================= */}
            {userInfo?.type === "recruiter" && (
              <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
                <div className="shrink-0 border-b border-gray-100 p-5">
                  <h2 className="text-base font-semibold text-gray-900">
                    Talent Summary
                  </h2>
                </div>

                {to_user_id?.name ? (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="min-h-0 flex-1 overflow-y-auto p-5">
                      <div className="text-center">
                        <img
                          src={
                            talentSummary?.photo || to_user_id?.photo || avatar
                          }
                          alt="Talent"
                          className="mx-auto h-16 w-16 rounded-full object-cover"
                        />
                        <h3 className="mt-3 text-sm font-semibold text-gray-900">
                          {talentSummary?.name || to_user_id.name}
                        </h3>
                      </div>

                      <div className="mt-6 space-y-4">
                        <div>
                          <p className="text-xs text-gray-400">Current job</p>
                          <p className="mt-1 text-sm text-gray-700">
                            {isSummaryLoading
                              ? "Loading..."
                              : formatSummaryValue(talentSummary?.current_job)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Job title</p>
                          <p className="mt-1 text-sm text-gray-700">
                            {isSummaryLoading
                              ? "Loading..."
                              : formatSummaryValue(talentSummary?.job_title)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Employer</p>
                          <p className="mt-1 text-sm text-gray-700">
                            {isSummaryLoading
                              ? "Loading..."
                              : formatSummaryValue(talentSummary?.employer)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">
                            Executive summary
                          </p>
                          <p
                            className={`mt-1 whitespace-pre-wrap text-sm ${
                              talentSummary?.executive_summary
                                ? "text-gray-700"
                                : "text-gray-400"
                            }`}
                          >
                            {isSummaryLoading
                              ? "Loading..."
                              : formatSummaryValue(
                                  talentSummary?.executive_summary,
                                )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {to_user_id?.id && to_user_id?.type === "talent" && (
                      <div className="shrink-0 border-t border-gray-100 p-5">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              routePath.talentDetails(
                                talentSummary?.talent_id || to_user_id.id,
                              ).path,
                            )
                          }
                          className="w-full rounded-lg border border-primary py-2.5 text-sm font-medium text-primary transition-colors duration-200 hover:bg-primary hover:text-white"
                        >
                          View Profile
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-5">
                    <p className="text-sm text-gray-400">
                      Select a conversation to view talent details.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Messages;
